// Чистая (без React) модель конструктора кампаний и шаблонов.
// Все функции не мутируют входные данные и возвращают новые объекты — это делает форму предсказуемой
// и позволяет покрыть логику unit-тестами (campaignFormModel.test.js).
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {MODE} from "./campaignMode";
import {convertToDayjs, formatDate, hasContent, isEmpty} from "../Utils/CommonUtils";
import {validateNumber, validateVersion} from "../Utils/Validator";
import {calculateTill, calculateTillFromDuration, getRecommendedDurationSeconds, isTillForDuration} from "./CampaignDuration";
import {hasAudienceValidationErrors, isUntouchedAudienceParameter, resetAudienceParameterErrors} from "./CampaignAudienceValidation";

dayjs.extend(utc);

export const STEPS = {
    BASIC: 'basic',
    DISTRIBUTION: 'distribution',
    AUDIENCE: 'audience',
    SUMMARY: 'summary',
};
export const STEP_ORDER = [STEPS.BASIC, STEPS.DISTRIBUTION, STEPS.AUDIENCE, STEPS.SUMMARY];

export const AUDIENCE_PREFIX = "Audience";
// 3000-01-01T00:00:00Z — бэкенд считает кампанию бессрочной при till >= этого значения.
// Раньше считалось из локальной строки "3000-01-01 03:00:00" и зависело от часового пояса браузера.
export const PERMANENT_TILL_UNIX = 32503680000;
export const TIME_ZONE = {displayName: "GMT+3 (Europe/Moscow)", value: "+03:00"};

const NUMBER_VERSION_OPERATORS = ['gt', 'lt', 'ge', 'le'];
export const POSSIBLE_OPERATORS = new Map(Object.entries({
    'NUMBER_RANGE': NUMBER_VERSION_OPERATORS,
    'VERSION_RANGE': NUMBER_VERSION_OPERATORS,
    'INCLUDE': 'include',
    'INCLUDE_ANY': 'includeAny',
    'INCLUDE_ALL': 'includeAll',
    'EXCLUDE': 'exclude',
    'EXCLUDE_ANY': 'excludeAny',
    'EXCLUDE_ALL': 'excludeAll',
    'OFFER_IN_PROFILE': 'offerInProfile',
    'OFFER_NOT_IN_PROFILE': 'offerNotInProfile',
    'PROFILE_OFFSET': 'profileOffset',
    'TYPE_INACTIVE': 'offerTypeInactiveRule',
    'TAG_INACTIVE': 'offerTagInactiveRule',
    'TAG_NOT_IN_PROFILE': 'offerTagNotInProfileRule',
    'TAGS_IN_PROFILE': 'offerTagsInProfileRule',
    'OFFER_INACTIVE': 'offerInactiveRule',
    'MATCH': 'matchRule',
    'NOT_MATCH': 'notMatchRule'
}));

export const PROFILE_OFFSET_OPERATORS = ['GREATER_THAN', 'LESS_THAN'];

// ---------------------------------------------------------------------------
// Параметры аудиторий
// ---------------------------------------------------------------------------

export class VersionNumericParameterSet {
    constructor(allowedRules, parameterName, paramFromValue = "", paramToValue = "", fromIncl = true, toIncl = false, operator = 'gt') {
        this.paramFromValue = paramFromValue;
        this.parameterData.allowedRules = allowedRules;
        this.paramToValue = paramToValue;
        this.parameterData.parameterName = parameterName;
        this.fromIncl = fromIncl;
        this.toIncl = toIncl;
        this.operator = operator;
    }

    parameterData = {
        allowedRules: [],
        parameterName: "",
    };
}

export class ParametersSet {
    constructor(allowedRules, allowedValues, isMain, parameterName, operator, paramValue = '', offerId = '', offsetValue = '', tags = '') {
        this.parameterData.allowedRules = allowedRules;
        this.parameterData.allowedValues = allowedValues;
        this.parameterData.isMain = isMain;
        this.parameterData.parameterName = parameterName;
        this.operator = operator;
        this.paramValue = paramValue;
        this.offerId = offerId;
        this.tags = tags;
        this.offsetValue = offsetValue;
    }

    parameterData = {
        allowedRules: [],
        allowedValues: [],
        isMain: false,
        parameterName: "",
    };
    operator = "";
    paramValue = "";
}

export const isVersionNumericParameter = (parameter) =>
    parameter.allowedRules.includes('NUMBER_RANGE') || parameter.allowedRules.includes('VERSION_RANGE');

export const isRangeParameter = (parameterSet) =>
    parameterSet.hasOwnProperty('fromIncl') || parameterSet.hasOwnProperty('toIncl');

const hasRule = (parameterSet, rule) => !!parameterSet?.parameterData?.allowedRules?.includes(rule);

export const createAudience = (number) => ({
    name: `${AUDIENCE_PREFIX} ${number}`,
    count: number,
    parameters: [new ParametersSet()],
});

// ---------------------------------------------------------------------------
// Новые кампания / шаблон
// ---------------------------------------------------------------------------

// Текущая минута без секунд и миллисекунд (раньше — момент загрузки модуля, с миллисекундами).
export const currentMinute = () => dayjs().startOf('minute');

export class Campaign {
    campaignId = "";
    groupId = "";
    fickle = false;
    repeatable = false;
    priority = "0";
    offerType = "";
    bundleId = "";
    timeZone = "";
    from = currentMinute();
    till = currentMinute();
    distributionType = "";
    durationHour = "";
    tags = [];
    audiences = [createAudience(1)];
}

export class CampaignTemplate {
    campaignId = "";
    groupId = "";
    fickle = false;
    priority = "";
    offerType = "";
    bundleId = "";
    timeZone = "";
    from = currentMinute();
    till = currentMinute();
    distributionType = "";
    durationHour = "";
    schedule = {
        delayDays: 0,
        till: null
    };
    audiences = [createAudience(1)];
}

// ---------------------------------------------------------------------------
// Шаг "basic"
// ---------------------------------------------------------------------------

export const isTohClanTournamentType = (offerType, types, domainAlias) =>
    domainAlias === 'toh' && types?.[offerType]?.interactionType === 'CLAN_TOURNAMENT';

export const requiresMatchmakingType = (campaign, types) => {
    const type = types?.[campaign.offerType];
    return !!type
        && ['TOURNAMENT', 'TEAM_TOURNAMENT'].includes(type.interactionType)
        && Array.isArray(type.matchmakingTypes) && type.matchmakingTypes.length > 0;
};

/** Автогенерация id/group по бандлу (кроме режима редактирования). */
export const generateCampaignId = ({bundleId, offerType, types, domainAlias, now = Date.now()}) => {
    if (domainAlias === 'cm' || domainAlias === 'staging') {
        return formatDate(now, 'YYYYMMDD') + '_' + bundleId;
    }
    const abbreviation = types?.[offerType]?.metaData?.abbreviation;
    const suffix = abbreviation !== '' && abbreviation != null ? '_' + abbreviation : '';
    return formatDate(now, 'YYYY_MM_DD') + suffix + '_' + bundleId;
};

export const applyBundle = (campaign, bundleId, {mode, types, domainAlias, now}) => {
    const next = {...campaign, bundleId};
    if (mode !== MODE.UPDATE) {
        const id = generateCampaignId({bundleId, offerType: campaign.offerType, types, domainAlias, now});
        next.campaignId = id;
        next.groupId = id;
    }
    return next;
};

export const applyOfferType = (campaign, offerType, {types, domainAlias}) => {
    const next = {...campaign, offerType};
    if (types && types.hasOwnProperty(offerType)) next.regexp = types[offerType].regexp;
    const recommended = getRecommendedDurationSeconds(types?.[offerType]);
    if (recommended !== null) next.till = calculateTillFromDuration(next.from, recommended);
    if (!isTohClanTournamentType(offerType, types, domainAlias)) next.previuosCampaignId = "";
    return next;
};

/** Изменение campaignId синхронно меняет groupId (как и раньше). */
export const applyField = (campaign, name, value) => {
    if (name === 'durationHour' && value !== '' && !/^[0-9\b]+$/.test(value)) return campaign;
    const next = {...campaign, [name]: value};
    if (name === 'campaignId') next.groupId = value;
    if (name === 'distributionType') {
        next.durationHour = '';
        if (value === 'permanent') {
            next.till = dayjs.unix(PERMANENT_TILL_UNIX);
            next.durationHour = 0;
        }
    }
    return next;
};

export const validateBasic = (campaign, {template} = {}) => {
    const errors = new Set();
    if (!hasContent(campaign.campaignId)) errors.add('campaignIdRequired');
    if (!hasContent(campaign.offerType)) errors.add('offerType');
    if (!hasContent(campaign.priority) || !/^[0-9]+$/.test(String(campaign.priority))) errors.add('priority');
    if (campaign.campaignId && campaign.campaignId.includes('(Clone)')) errors.add('(Clone)');
    if (!template && campaign.regexp !== '*' && !isEmpty(campaign.regexp)) {
        if (!new RegExp(campaign.regexp).test(campaign.campaignId)) errors.add('regexp');
    }
    return errors;
};

// ---------------------------------------------------------------------------
// Шаг "distribution"
// ---------------------------------------------------------------------------

export const applyDate = (campaign, field, date, {types}) => {
    const value = dayjs(new Date(date.set('second', 0).valueOf()));
    const next = {...campaign, [field]: value};
    const recommended = getRecommendedDurationSeconds(types?.[campaign.offerType]);
    if (field === 'from' && isTillForDuration(campaign.from, campaign.till, recommended)) {
        next.till = calculateTillFromDuration(value, recommended);
    }
    next[field + 'Instant'] = Date.parse(date.toISOString().replace('Z', campaign.timeZone));
    return next;
};

export const applyTillDays = (campaign, days) => ({...campaign, till: calculateTill(campaign.from, days)});

export const applyTillDuration = (campaign, seconds) => ({...campaign, till: calculateTillFromDuration(campaign.from, seconds)});

export const applyScheduleField = (campaign, name, value) => ({...campaign, schedule: {...campaign.schedule, [name]: value}});

export const applyScheduleTill = (campaign, date) => {
    try {
        const value = dayjs(new Date(date.set('second', 0).valueOf()));
        return {
            ...campaign,
            schedule: {...campaign.schedule, till: value, tillInstant: Date.parse(date.toISOString().replace('Z', campaign.timeZone))},
        };
    } catch (e) {
        return {...campaign, schedule: {...campaign.schedule, till: null, tillInstant: null}};
    }
};

export const validateDistribution = (campaign) => {
    const errors = new Set();
    if (!hasContent(campaign.distributionType)) errors.add('distributionType');
    if (!campaign.from.isBefore(campaign.till)) errors.add('date');
    if (campaign.distributionType !== 'fixed' && campaign.distributionType !== 'permanent'
        && !/^\d+$/.test(String(campaign.durationHour ?? ''))) {
        errors.add('durationHour');
    }
    return errors;
};

export const getAnnounceFrom = (campaign, typeParameters) =>
    dayjs(campaign.from.unix() * 1000 - (typeParameters?.announceDuration || 0) * 1000);

export const getExtraTill = (campaign, typeParameters) =>
    dayjs(campaign.till.unix() * 1000 + (typeParameters?.extraDuration || 0) * 1000);

/** Даты следующих запусков шаблона (пример в шаге distribution). */
export const getTemplateRuns = (campaign) => {
    const delayDays = Number(campaign.schedule?.delayDays);
    if (!delayDays) return null;
    const format = ms => dayjs(ms).utcOffset(180).format('YYYY-MM-DD HH:mm:ss');
    const fromMs = campaign.from.valueOf();
    const tillMs = campaign.till.valueOf();
    const endMs = campaign.schedule.till ? campaign.schedule.till.valueOf() : NaN;
    const periodMs = delayDays * 86400000;
    const run = startMs => ({date: format(startMs), pair: format(tillMs + (startMs - fromMs))});

    const second = fromMs + 2 * periodMs;
    let lastMs = fromMs;
    while (lastMs + periodMs <= endMs) lastMs += periodMs;

    return {
        next: run(fromMs + periodMs),
        afterNext: second <= endMs || isNaN(endMs) ? run(second) : null,
        last: run(lastMs),
    };
};

// ---------------------------------------------------------------------------
// Шаг "audience"
// ---------------------------------------------------------------------------

const replaceAudience = (campaign, audienceIndex, updater) => ({
    ...campaign,
    audiences: campaign.audiences.map((audience, index) => index === audienceIndex ? updater(audience) : audience),
});

const replaceParameter = (campaign, audienceIndex, paramIndex, updater) => replaceAudience(campaign, audienceIndex, audience => ({
    ...audience,
    parameters: audience.parameters.map((parameter, index) => index === paramIndex ? updater(parameter) : parameter),
}));

/** Наименьший свободный номер аудитории (числовая сортировка: раньше 10 < 2). */
export const nextAudienceNumber = (audiences, fallback) => {
    const numbers = audiences.map(a => a.count).sort((a, b) => a - b);
    const max = numbers[numbers.length - 1];
    for (let i = 1; i < max; i++) {
        if (!numbers.includes(i)) return i;
    }
    return fallback;
};

export const addAudience = (campaign) => {
    const number = nextAudienceNumber(campaign.audiences, campaign.audiences.length + 1);
    return {...campaign, audiences: [...campaign.audiences, createAudience(number)]};
};

export const cloneAudience = (campaign, sourceIndex) => {
    const number = nextAudienceNumber(campaign.audiences, campaign.audiences.length + 1);
    const clone = JSON.parse(JSON.stringify(campaign.audiences[sourceIndex]));
    clone.name = `${AUDIENCE_PREFIX} ${number}`;
    clone.count = number;
    return {...campaign, audiences: [...campaign.audiences, clone]};
};

export const deleteAudience = (campaign, index) => campaign.audiences.length > 1
    ? {...campaign, audiences: campaign.audiences.filter((_, i) => i !== index)}
    : campaign;

export const addParameter = (campaign, audienceIndex) => replaceAudience(campaign, audienceIndex, audience => ({
    ...audience,
    parameters: [...audience.parameters, new ParametersSet()],
}));

export const deleteParameter = (campaign, audienceIndex, paramIndex) => replaceAudience(campaign, audienceIndex, audience => ({
    ...audience,
    parameters: audience.parameters.filter((_, i) => i !== paramIndex),
}));

/** Выбор параметра: новый набор нужного вида + оператор по умолчанию (первое разрешённое правило). */
export const setParameterData = (campaign, availableParams, parameterName, audienceIndex, paramIndex) => {
    const param = availableParams.find(p => p.parameterName === parameterName);
    if (!param) return campaign;
    return replaceParameter(campaign, audienceIndex, paramIndex, () => {
        const parameterSet = isVersionNumericParameter(param) ? new VersionNumericParameterSet() : new ParametersSet();
        const defaultOperator = POSSIBLE_OPERATORS.get(param.allowedRules[0]);
        parameterSet.parameterData = param;
        parameterSet.operator = typeof defaultOperator === 'string' ? defaultOperator : defaultOperator[0];
        return parameterSet;
    });
};

export const updateParameter = (campaign, audienceIndex, paramIndex, patch) =>
    replaceParameter(campaign, audienceIndex, paramIndex, parameter => Object.assign(
        Object.create(Object.getPrototypeOf(parameter)), parameter, patch
    ));

const withErrorFlags = (parameter, flags) => Object.assign(Object.create(Object.getPrototypeOf(parameter)), parameter, flags);

const isInvalidRange = (parameter) => {
    if (!parameter.paramFromValue && !parameter.paramToValue) return false;
    return (hasRule(parameter, 'VERSION_RANGE') && !validateVersion(parameter.paramFromValue, parameter.paramToValue))
        || (hasRule(parameter, 'NUMBER_RANGE') && !validateNumber(parameter.paramFromValue, parameter.paramToValue));
};

const flagParameter = (parameter, template) => {
    if (template && isUntouchedAudienceParameter(parameter)) {
        const clean = withErrorFlags(parameter, {});
        resetAudienceParameterErrors(clean);
        return clean;
    }
    if (isRangeParameter(parameter)) return parameter;
    if (hasRule(parameter, 'PROFILE_OFFSET')) {
        // раньше для нового offset-параметра ошибка уходила в невидимый valueError
        return withErrorFlags(parameter, {
            offerValueError: !parameter.offerId,
            offsetValueError: !parameter.offset,
            operatorValueError: !PROFILE_OFFSET_OPERATORS.includes(parameter.operator),
            valueError: false,
            operatorError: false,
        });
    }
    return withErrorFlags(parameter, {
        operatorError: !parameter.operator,
        valueError: !parameter.paramValue || parameter.paramValue.length === 0,
    });
};

// Параметры иммутабельны: результат для неизменённого параметра переиспользуется,
// поэтому строки, которые не редактировали, сохраняют ссылку и не перерисовываются.
const flaggedCache = {true: new WeakMap(), false: new WeakMap()};
const flagParameterCached = (parameter, template) => {
    if (!parameter || typeof parameter !== 'object') return parameter;
    const cache = flaggedCache[!!template];
    if (!cache.has(parameter)) cache.set(parameter, flagParameter(parameter, template));
    return cache.get(parameter);
};

/**
 * Валидация аудиторий. Возвращает аудитории с флагами ошибок на параметрах (valueError, operatorError,
 * offerValueError, offsetValueError, operatorValueError), ошибки диапазонов
 * ({audience, paramIndex, parameterName}) и общий признак hasErrors.
 */
export const validateAudiences = (audiences, {template} = {}) => {
    const rangeErrors = [];
    const validated = audiences.map((audience, audienceIndex) => {
        audience.parameters.forEach((parameter, paramIndex) => {
            if (isRangeParameter(parameter) && isInvalidRange(parameter)) {
                rangeErrors.push({audience: audienceIndex, paramIndex, parameterName: parameter.parameterData.parameterName});
            }
        });
        const parameters = audience.parameters.map(parameter => flagParameterCached(parameter, template));
        const changed = parameters.some((parameter, index) => parameter !== audience.parameters[index]);
        return changed ? {...audience, parameters} : audience;
    });

    const errorAudienceIndexes = validated
        .map((audience, index) => hasAudienceValidationErrors([audience], template) || rangeErrors.some(e => e.audience === index) ? index : -1)
        .filter(index => index >= 0);

    return {
        audiences: validated,
        rangeErrors,
        errorAudienceIndexes,
        hasErrors: errorAudienceIndexes.length > 0,
    };
};

// ---------------------------------------------------------------------------
// Сохранение
// ---------------------------------------------------------------------------

const emptyAudienceForSave = () => ({
    includeAll: [], includeAny: [], excludeAll: [], excludeAny: [],
    numberRange: [], versionRange: [],
    offerTypeInactiveRule: [], offerTagInactiveRule: [], offerTagNotInProfileRule: [], offerTagsInProfileRule: [],
    offerInactiveRule: [], offerNotInProfile: [], offerInProfile: [], profileOffset: [],
    matchRule: [], notMatchRule: [],
});

const LIST_RULES = ['INCLUDE', 'INCLUDE_ALL', 'INCLUDE_ANY', 'EXCLUDE_ANY', 'EXCLUDE_ALL', 'EXCLUDE'];

const appendParameter = (audience, parameterSet) => {
    const name = parameterSet.parameterData?.parameterName;
    const range = () => ({
        parameter: name,
        from: parameterSet.paramFromValue,
        to: parameterSet.paramToValue,
        fromIncluded: parameterSet.fromIncl,
        toIncluded: parameterSet.toIncl
    });

    if (hasRule(parameterSet, 'VERSION_RANGE')) {
        audience.versionRange.push(range());
    } else if (hasRule(parameterSet, 'NUMBER_RANGE')) {
        audience.numberRange.push(range());
    } else if (LIST_RULES.some(rule => hasRule(parameterSet, rule))) {
        // legacy include/exclude отсутствуют в пустой аудитории — раньше здесь было падение
        (audience[parameterSet.operator] = audience[parameterSet.operator] || []).push({
            parameter: name,
            values: typeof parameterSet.paramValue === 'string' ? parameterSet.paramValue.split(',') : parameterSet.paramValue
        });
    } else if (hasRule(parameterSet, 'OFFER_IN_PROFILE')) {
        audience.offerInProfile.push({parameter: 'offers_in_profile', offerId: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'OFFER_INACTIVE')) {
        audience.offerInactiveRule.push({parameter: 'offer_inactive', offerIds: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'TYPE_INACTIVE')) {
        audience.offerTypeInactiveRule.push({parameter: 'type_inactive', types: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'TAG_INACTIVE')) {
        audience.offerTagInactiveRule.push({parameter: 'tag_inactive', tags: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'TAG_NOT_IN_PROFILE')) {
        audience.offerTagNotInProfileRule.push({parameter: 'tag_not_in_profile', tags: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'TAGS_IN_PROFILE')) {
        audience.offerTagsInProfileRule.push({parameter: 'tags_in_profile', tags: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'OFFER_NOT_IN_PROFILE')) {
        audience.offerNotInProfile.push({parameter: 'offers_not_in_profile', offerId: parameterSet.paramValue});
    } else if (parameterSet.operator === 'MATCH' || parameterSet.operator === 'matchRule'
        || (!parameterSet.operator && hasRule(parameterSet, 'MATCH'))) {
        audience.matchRule.push({parameter: 'adjust_campaign', regExp: parameterSet.paramValue});
    } else if (parameterSet.operator === 'NOT_MATCH' || parameterSet.operator === 'notMatchRule'
        || (!parameterSet.operator && hasRule(parameterSet, 'NOT_MATCH'))) {
        audience.notMatchRule.push({parameter: 'adjust_campaign', regExp: parameterSet.paramValue});
    } else if (hasRule(parameterSet, 'PROFILE_OFFSET')) {
        audience.profileOffset.push({
            offerId: parameterSet.offerId,
            offset: parameterSet.offset,
            operator: parameterSet.operator,
            parameter: name
        });
    }
    return audience;
};

export const audienceToSave = (audience) => audience.parameters.reduce(appendParameter, emptyAudienceForSave());

export const buildSavePayload = (campaign, {template, isTohClan} = {}) => {
    const payload = {
        id: campaign.campaignId,
        fickle: campaign.fickle,
        repeatable: campaign.repeatable,
        groupId: campaign.groupId,
        priority: campaign.priority,
        type: campaign.offerType,
        bundleId: campaign.bundleId,
        distribution: {
            type: campaign.distributionType,
            durationHour: campaign.durationHour ? campaign.durationHour : "",
            from: campaign.from.valueOf() / 1000,
            till: campaign.till.valueOf() / 1000
        },
        audiences: campaign.audiences.map(audienceToSave),
        tags: campaign.tags == undefined ? null : campaign.tags.length === 0 ? null : campaign.tags
    };
    if (!isEmpty(campaign.matchmakingType)) payload.matchmakingType = campaign.matchmakingType;
    if (isTohClan && !isEmpty(campaign.previuosCampaignId)) payload.previuosCampaignId = campaign.previuosCampaignId;
    if (campaign.tournamentId !== null && campaign.tournamentId !== undefined) payload.tournamentId = campaign.tournamentId;
    if (template) {
        payload.schedule = {
            delayDays: campaign.schedule.delayDays,
            till: campaign.schedule.till != null ? campaign.schedule.till.valueOf() / 1000 : null
        };
    }
    return payload;
};

// ---------------------------------------------------------------------------
// Загрузка существующей кампании / шаблона (edit / clone / template from campaign)
// ---------------------------------------------------------------------------

const LIST_OPERATORS = ['include', 'exclude', 'includeAll', 'includeAny', 'excludeAll', 'excludeAny'];
const RULE_LOADERS = [
    ['matchRule', 'MATCH', item => item.regExp],
    ['notMatchRule', 'NOT_MATCH', item => item.regExp],
    ['offerTypeInactiveRule', 'TYPE_INACTIVE', item => item.types],
    ['offerTagInactiveRule', 'TAG_INACTIVE', item => item.tags],
    ['offerTagNotInProfileRule', 'TAG_NOT_IN_PROFILE', item => item.tags],
    ['offerTagsInProfileRule', 'TAGS_IN_PROFILE', item => item.tags],
    ['offerInactiveRule', 'OFFER_INACTIVE', item => item.offerIds],
    ['offerInProfile', 'OFFER_IN_PROFILE', item => item.offerId],
    ['offerNotInProfile', 'OFFER_NOT_IN_PROFILE', item => item.offerId],
];

export const parametersFromApiAudience = (apiAudience) => {
    const audience = {...apiAudience};
    const parameters = [];

    (audience.versionRange || []).forEach(item => parameters.push(new VersionNumericParameterSet(
        ['VERSION_RANGE'], item.parameter, item.from ? item.from : null, item.to ? item.to : null, item.fromIncluded, item.toIncluded
    )));
    (audience.numberRange || []).forEach(item => parameters.push(new VersionNumericParameterSet(
        ['NUMBER_RANGE'], item.parameter, item.from, item.to, item.fromIncluded, item.toIncluded
    )));

    LIST_OPERATORS.forEach(operator => {
        if (audience[operator] != null) {
            audience[operator].forEach(item => {
                const rules = item.parameter === 'country' ? ['INCLUDE', 'EXCLUDE'] : ['INCLUDE'];
                parameters.push(new ParametersSet(rules, [], null, item.parameter, operator, item.values));
            });
        }
        if (audience[operator] == null) delete audience[operator];
    });

    RULE_LOADERS.forEach(([key, rule, getValue]) => {
        (audience[key] || []).forEach(item => {
            parameters.push(new ParametersSet([rule], [], null, item.parameter, key, getValue(item)));
        });
    });

    (audience.profileOffset || []).forEach(item => parameters.push({
        parameterData: {parameterName: item.parameter, allowedRules: ['PROFILE_OFFSET']},
        isMain: null,
        operator: item.operator,
        parameter: item.parameter,
        offerId: item.offerId,
        offset: item.offset,
    }));

    if (Object.values(audience).every(value => value.length === 0)) parameters.push(new ParametersSet());
    return parameters;
};

export const campaignFromApi = (source, {mode, template}) => {
    const campaign = {
        campaignId: mode === MODE.CLONE ? source.id + ' (Clone)' : source.id,
        groupId: source.groupId,
        fickle: source.fickle,
        tag: source.tag,
        tags: source.tags,
        repeatable: source.repeatable,
        priority: source.priority,
        offerType: source.type,
        bundleId: source.bundleId,
        matchmakingType: source.matchmakingType,
        previuosCampaignId: source.previuosCampaignId ?? "",
        timeZone: TIME_ZONE.displayName,
        from: convertToDayjs(source.distribution.from),
        till: convertToDayjs(source.distribution.till),
        distributionType: source.distribution.type,
        durationHour: source.distribution.durationHour,
        audiences: source.audiences.map((audience, index) => ({
            name: `${AUDIENCE_PREFIX} ${index + 1}`,
            count: index + 1,
            parameters: parametersFromApiAudience(audience),
        })),
        tournamentId: source.tournamentId,
    };
    if (template) {
        campaign.schedule = mode === MODE.POLY
            ? {till: null, delayDays: 0}
            : {
                till: source.schedule.till !== null ? convertToDayjs(source.schedule.till) : null,
                delayDays: source.schedule.delayDays,
            };
    }
    return campaign;
};

// ---------------------------------------------------------------------------
// Доступность кнопки "Next"
// ---------------------------------------------------------------------------

/**
 * Причина, по которой нельзя перейти дальше (кнопка заблокирована), или null.
 * В режиме редактирования кнопка не блокируется — проверки выполняются по нажатию.
 */
export const getNextBlockReason = ({campaign, mode, step, types, idCheck, audienceHasErrors}) => {
    if (mode === MODE.UPDATE) return null;
    if (step === STEPS.AUDIENCE && audienceHasErrors) return 'Fix audience errors';
    if (!campaign.campaignId) return 'Fill in Campaign ID';
    if (!types) return 'Loading campaign types…';
    if (!types.hasOwnProperty(campaign.offerType)) return 'Choose a type';
    if (requiresMatchmakingType(campaign, types) && isEmpty(campaign.matchmakingType)) return 'Choose a matchmaking type';
    if (isEmpty(campaign.bundleId)) return 'Choose a bundle';
    if (idCheck.pending) return 'Checking Campaign ID…';
    if (!idCheck.isFree) return 'This Campaign ID is already taken';
    return null;
};
